import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudUpload, Undo } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { formatCityState } from '../utils/formatCityState';
import {
  DataTableShell,
  EmptyState,
  InlineMessage,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
  TableSkeleton,
} from '../components/ui';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { CsvTemplateHint } from '../components/ui';
import { mapSheetTemplateProps, mapSheetTemplateParam } from '../utils/mapSheetTemplate';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

/** Dealer and location options wrap instead of cutting off long "Name · City, ST" labels. */
const WRAPPING_MENU_PROPS = { PaperProps: { sx: { maxWidth: 'min(480px, calc(100vw - 32px))' } } };
const WRAPPING_OPTION_SX = { whiteSpace: 'normal', wordBreak: 'break-word' };

interface Dealer {
  organizationId: string;
  name: string;
  city: string | null;
  state: string | null;
  brands: Array<{ brandId: string; brandName: string }>;
  locations?: Array<{ id: string; name: string; city: string | null; state: string | null; isPrimary: boolean }>;
}

interface SentSheet {
  id: string;
  organizationId: string;
  storeName: string;
  targetBrandName: string | null;
  pricingScope?: { kind: 'all' | 'location' | 'integration'; locationId?: string | null } | null;
  fileName: string;
  status: string;
  orgApprovalStatus: string;
  recordsFound: number;
  warnings: string[];
  errors: string[];
  rejectionReason: string | null;
  createdAt: string | null;
  orgApprovedAt: string | null;
}

interface VendorRebateProgram {
  id: string;
  name: string;
  brand: string;
  kind: 'instant_rebate' | 'trade_credit' | 'bundle';
  organizationId: string | null;
  organizationName: string | null;
  scope: { kind: 'all' | 'location' | 'integration' | 'zone'; locationId?: string | null; integrationId?: string | null };
  startAt: string | null;
  endAt: string | null;
  status: 'draft' | 'pending_store_approval' | 'active' | 'declined' | 'expired';
}

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

const sheetChip = (sheet: SentSheet) => {
  if (sheet.orgApprovalStatus === 'approved') return <StatusChip label="Accepted" tone="positive" size="small" />;
  if (sheet.status === 'rejected') return <StatusChip label={sheet.rejectionReason === 'Withdrawn by the vendor' ? 'Withdrawn' : 'Declined'} tone="problem" size="small" />;
  if (sheet.status === 'duplicate') return <StatusChip label="Duplicate" tone="caution" size="small" />;
  return <StatusChip label="Waiting for the store" tone="caution" size="small" />;
};

/**
 * Read-only view of the rebate programs this vendor owns, whichever store
 * they were sent to — the store decides whether a program runs. Hidden
 * entirely while `rebate_programs` is off for this organization.
 */
const VendorRebateProgramsSection: React.FC<{ dealers: Dealer[] }> = ({ dealers }) => {
  const { t } = useAppTranslation();
  const isMobile = useIsMobile();
  const { enabled: rebateProgramsEnabled } = useOrgFeatureFlag('rebate_programs');

  const programs = useQuery({
    queryKey: ['vendor-rebate-programs'],
    queryFn: async () => {
      const response = await makeApiCall('/vendor/rebate-programs', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, t('vendor.rebatePrograms.loadError')));
      return ((await response.json()).data || []) as VendorRebateProgram[];
    },
    enabled: rebateProgramsEnabled,
    staleTime: 60_000,
  });

  if (!rebateProgramsEnabled) return null;

  const kindLabel = (kind: VendorRebateProgram['kind']) => t(`vendor.rebatePrograms.kind.${kind}`);

  const statusTone = (status: VendorRebateProgram['status']) => {
    if (status === 'pending_store_approval') return 'caution' as const;
    if (status === 'active') return 'positive' as const;
    if (status === 'declined') return 'problem' as const;
    return 'neutral' as const;
  };

  const scopeLabel = (program: VendorRebateProgram) => {
    if (program.scope.kind === 'all') return t('vendor.rebatePrograms.scope.all');
    if (program.scope.kind === 'location') {
      const name = dealers
        .find((d) => d.organizationId === program.organizationId)
        ?.locations?.find((l) => l.id === program.scope.locationId)?.name;
      return name || t('vendor.rebatePrograms.scope.location');
    }
    return t('vendor.rebatePrograms.scope.integration');
  };

  const windowLabel = (program: VendorRebateProgram) => {
    const start = program.startAt ? new Date(program.startAt).toLocaleDateString() : null;
    const end = program.endAt ? new Date(program.endAt).toLocaleDateString() : null;
    if (!start && !end) return t('vendor.rebatePrograms.windowOngoing');
    return `${start || t('vendor.rebatePrograms.windowOpenStart')} – ${end || t('vendor.rebatePrograms.windowOpenEnd')}`;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <SectionCard dense title={t('vendor.rebatePrograms.title')} subtitle={t('vendor.rebatePrograms.subtitle')}>
        {programs.isLoading ? (
          <TableSkeleton rows={3} columns={6} />
        ) : programs.isError ? (
          <InlineMessage severity="error">{(programs.error as Error).message}</InlineMessage>
        ) : !programs.data || programs.data.length === 0 ? (
          <EmptyState
            size="compact"
            title={t('vendor.rebatePrograms.emptyTitle')}
            description={t('vendor.rebatePrograms.emptyDescription')}
          />
        ) : isMobile ? (
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack divider={<Divider />}>
              {programs.data.map((program) => (
                <Box key={program.id} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2">{program.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {program.brand} · {kindLabel(program.kind)}
                      </Typography>
                    </Box>
                    <StatusChip
                      label={t(`vendor.rebatePrograms.status.${program.status}`)}
                      tone={statusTone(program.status)}
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                    <Box>
                      <Typography component="span" sx={mobileLabelSx}>{t('vendor.rebatePrograms.columns.store')}</Typography>
                      <Typography variant="body2">
                        {program.organizationName || t('vendor.rebatePrograms.globalProgram')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography component="span" sx={mobileLabelSx}>{t('vendor.rebatePrograms.columns.scope')}</Typography>
                      <Typography variant="body2">{scopeLabel(program)}</Typography>
                    </Box>
                    <Box>
                      <Typography component="span" sx={mobileLabelSx}>{t('vendor.rebatePrograms.columns.window')}</Typography>
                      <Typography variant="body2">{windowLabel(program)}</Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        ) : (
          <DataTableShell>
            <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('vendor.rebatePrograms.columns.name')}</TableCell>
                  <TableCell>{t('vendor.rebatePrograms.columns.brand')}</TableCell>
                  <TableCell>{t('vendor.rebatePrograms.columns.kind')}</TableCell>
                  <TableCell>{t('vendor.rebatePrograms.columns.store')}</TableCell>
                  <TableCell>{t('vendor.rebatePrograms.columns.scope')}</TableCell>
                  <TableCell>{t('vendor.rebatePrograms.columns.window')}</TableCell>
                  <TableCell>{t('vendor.rebatePrograms.columns.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.data.map((program) => (
                  <TableRow key={program.id} hover>
                    <TableCell>{program.name}</TableCell>
                    <TableCell>{program.brand}</TableCell>
                    <TableCell>{kindLabel(program.kind)}</TableCell>
                    <TableCell>{program.organizationName || t('vendor.rebatePrograms.globalProgram')}</TableCell>
                    <TableCell>{scopeLabel(program)}</TableCell>
                    <TableCell>{windowLabel(program)}</TableCell>
                    <TableCell>
                      <StatusChip label={t(`vendor.rebatePrograms.status.${program.status}`)} tone={statusTone(program.status)} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </DataTableShell>
        )}
      </SectionCard>
    </Box>
  );
};

/**
 * Priya's private dealer pricing: send a MAP / dealer price sheet to one
 * store that is verified for the brand. It applies to that store only, once
 * they accept it, and never becomes platform pricing.
 */
export const VendorDealerPricingPage: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { enabled: flagOn, isLoading: flagLoading } = useOrgFeatureFlag('vendor_private_pricing');
  const [dealerId, setDealerId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dealers = useQuery({
    queryKey: ['vendor-private-pricing-dealers'],
    queryFn: async () => {
      const response = await makeApiCall('/vendor/private-pricing/dealers', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, 'Failed to load verified dealers'));
      return ((await response.json()).data || []) as Dealer[];
    },
    enabled: flagOn,
  });

  const sent = useQuery({
    queryKey: ['vendor-private-pricing-submissions'],
    queryFn: async () => {
      const response = await makeApiCall('/vendor/private-pricing/submissions', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, 'Failed to load sent sheets'));
      return ((await response.json()).data || []) as SentSheet[];
    },
    enabled: flagOn,
  });

  const selectedDealer = useMemo(() => (dealers.data || []).find((d) => d.organizationId === dealerId) || null, [dealers.data, dealerId]);
  const brandOptions = selectedDealer?.brands || [];

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const headers = await getAuthHeaders();
      delete (headers as Record<string, string>)['Content-Type'];
      // A named location goes as locationId; "All locations" (and no
      // location picker) must send pricingScope=all — the route no longer
      // defaults an unnamed scope, and rebate sheets are refused without one.
      const scopeQuery = locationId
        ? `&locationId=${encodeURIComponent(locationId)}`
        : '&pricingScope=all';
      // A CSV in the downloadable template layout is read by the canonical sheet parser.
      const templateQuery = (await mapSheetTemplateParam(file)) ? '&template=motzu' : '';
      const response = await makeApiCall(
        `/vendor/private-pricing/upload?organizationId=${encodeURIComponent(dealerId)}&brandId=${encodeURIComponent(brandId)}${scopeQuery}${templateQuery}`,
        { method: 'POST', headers, body: formData }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.data?.errors?.[0] || body?.error?.message || body?.message || 'Upload failed');
      return body;
    },
    onSuccess: (body) => {
      setMessage(body.message || 'Sent');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['vendor-private-pricing-submissions'] });
    },
    onError: (e: any) => setError(e?.message || 'Upload failed'),
  });

  const withdraw = useMutation({
    mutationFn: async (id: string) => {
      const response = await makeApiCall(`/vendor/private-pricing/submissions/${id}`, { method: 'DELETE', headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, 'Withdraw failed'));
      return response.json();
    },
    onSuccess: () => {
      setMessage('Sheet withdrawn');
      queryClient.invalidateQueries({ queryKey: ['vendor-private-pricing-submissions'] });
    },
    onError: (e: any) => setError(e?.message || 'Withdraw failed'),
  });

  if (flagLoading) {
    return (
      <PageContainer>
        <CircularProgress />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Dealer Pricing"
        subtitle="Send a private MAP or dealer price sheet to one store. It applies to that store only, after they accept it, and never becomes platform pricing."
      />
      {!flagOn && (
        <Alert severity="info">Private dealer pricing is not enabled for your organization yet. Ask Motzu to turn it on.</Alert>
      )}
      {flagOn && (
        <>
          {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {dealers.error && <Alert severity="error" sx={{ mb: 2 }}>{(dealers.error as Error).message}</Alert>}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Send a sheet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Only stores verified as your authorized dealers are listed. Use your usual MAP sheet layout; rows for other brands are dropped.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="dealer-label">Dealer store</InputLabel>
                  <Select labelId="dealer-label" label="Dealer store" MenuProps={WRAPPING_MENU_PROPS} value={dealerId} onChange={(e) => { setDealerId(e.target.value); setBrandId(''); setLocationId(''); }}>
                    {(dealers.data || []).map((d) => (
                      <MenuItem key={d.organizationId} value={d.organizationId} sx={WRAPPING_OPTION_SX}>
                        {d.name}{formatCityState(d.city, d.state) ? ` · ${formatCityState(d.city, d.state)}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedDealer}>
                  <InputLabel id="brand-label">Brand</InputLabel>
                  <Select labelId="brand-label" label="Brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
                    {brandOptions.map((b) => (
                      <MenuItem key={b.brandId} value={b.brandId}>{b.brandName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedDealer && (selectedDealer.locations || []).length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="location-label">Location</InputLabel>
                    <Select labelId="location-label" label="Location" MenuProps={WRAPPING_MENU_PROPS} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                      <MenuItem value="">All locations</MenuItem>
                      {(selectedDealer.locations || []).map((l) => (
                        <MenuItem key={l.id} value={l.id} sx={WRAPPING_OPTION_SX}>{l.name}{formatCityState(l.city, l.state) ? ` · ${formatCityState(l.city, l.state)}` : ''}{l.isPrimary ? ' (primary)' : ''}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".xlsx,.xlsm,.xls,.csv,.pdf"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }}
                />
                <Button
                  variant="contained"
                  startIcon={upload.isPending ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
                  disabled={!dealerId || !brandId || upload.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {upload.isPending ? 'Sending…' : 'Choose file and send'}
                </Button>
              </Stack>
              <Box sx={{ mt: 2 }}>
                <CsvTemplateHint {...mapSheetTemplateProps(t, 'vendorSheet')} />
              </Box>
              {dealers.isSuccess && dealers.data.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>No store is verified for your brand yet. A store unlocks here once its brand authorization is verified.</Alert>
              )}
            </CardContent>
          </Card>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Sent sheets</Typography>
          {sent.isLoading ? (
            <CircularProgress />
          ) : !sent.data || sent.data.length === 0 ? (
            <EmptyState title="Nothing sent yet" description="Sheets you send to dealers show here with whether the store accepted them." size="compact" />
          ) : isMobile ? (
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <Stack divider={<Divider />}>
                {sent.data.map((sheet) => {
                  const appliesTo = sheet.pricingScope?.kind === 'location'
                    ? (dealers.data || []).find((d) => d.organizationId === sheet.organizationId)?.locations?.find((l) => l.id === sheet.pricingScope?.locationId)?.name || 'One location'
                    : 'All locations';
                  return (
                    <Box key={sheet.id} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2">{sheet.storeName}</Typography>
                          <Typography variant="caption" color="text.secondary">{sheet.targetBrandName || '—'}</Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>{sheetChip(sheet)}</Box>
                      </Stack>
                      <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Applies to</Typography>
                          <Typography variant="body2">{appliesTo}</Typography>
                        </Box>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Rows</Typography>
                          <Typography variant="body2">{sheet.recordsFound}</Typography>
                        </Box>
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Sent</Typography>
                          <Typography variant="body2">{sheet.createdAt ? new Date(sheet.createdAt).toLocaleDateString() : '—'}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{sheet.fileName}</Typography>
                        {sheet.warnings.length > 0 && (
                          <Tooltip title={sheet.warnings.join('; ')}><Chip size="small" variant="outlined" label="⚠" /></Tooltip>
                        )}
                      </Stack>
                      {sheet.orgApprovalStatus !== 'approved' && sheet.status !== 'rejected' && (
                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                          <Button size="small" startIcon={<Undo />} disabled={withdraw.isPending} onClick={() => withdraw.mutate(sheet.id)}>
                            Withdraw
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Applies to</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell align="right">Rows</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sent.data.map((sheet) => (
                    <TableRow key={sheet.id} hover>
                      <TableCell>{sheet.storeName}</TableCell>
                      <TableCell>{sheet.targetBrandName || '—'}</TableCell>
                      <TableCell>
                        {sheet.pricingScope?.kind === 'location'
                          ? (dealers.data || []).find((d) => d.organizationId === sheet.organizationId)?.locations?.find((l) => l.id === sheet.pricingScope?.locationId)?.name || 'One location'
                          : 'All locations'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{sheet.fileName}</span>
                          {sheet.warnings.length > 0 && (
                            <Tooltip title={sheet.warnings.join('; ')}><Chip size="small" variant="outlined" label="⚠" /></Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{sheet.recordsFound}</TableCell>
                      <TableCell>{sheetChip(sheet)}</TableCell>
                      <TableCell>{sheet.createdAt ? new Date(sheet.createdAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="right">
                        {sheet.orgApprovalStatus !== 'approved' && sheet.status !== 'rejected' && (
                          <Button size="small" startIcon={<Undo />} disabled={withdraw.isPending} onClick={() => withdraw.mutate(sheet.id)}>
                            Withdraw
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              An accepted sheet outranks the store's own uploaded sheets for the same products. The store's per-product overrides still win.
            </Typography>
          </Box>

          <VendorRebateProgramsSection dealers={dealers.data || []} />
        </>
      )}
    </PageContainer>
  );
};

export default VendorDealerPricingPage;
